import { useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { format } from 'date-fns';
import _get from 'lodash/get';
import { Link as RouterLink } from 'react-router-dom';
import CustomLink from '@/components/general/CustomLink';
import CustomTable from '@/components/general/CustomTable';
import CustomTableCell from '@/components/general/CustomTableCell';
import CustomTableRow from '@/components/general/CustomTableRow';
import PageTitle from '@/components/general/PageTitle';
import { getEndpointWithParams } from '@/routes/config';
import { HeaderTable, CheckinButtonLabel } from '@/types';
import { UserCheckin, CheckinSummary } from '@/types/api';
import { Paths } from '@/types/routes';
import { defaultCheckinDateFormat } from '@/utils/checkins';
import styles from './UserCheckins.styles';

type UserCheckinsPageProps = {
  checkins: CheckinSummary[];
  isLoggedUserCheckinsPage: boolean;
  userId?: string;
  username?: string;
};

const UserCheckinsPage = function ({
  checkins,
  isLoggedUserCheckinsPage,
  userId,
  username = 'User',
}: UserCheckinsPageProps): JSX.Element {
  const arrayHeaders: HeaderTable<CheckinSummary>[] = useMemo(
    () => [
      { id: 'dueDate', label: 'Due Date' },
      { id: 'answeredAt', label: 'Submitted date' },
      null,
    ],
    []
  );

  const checkinsFormatted = useMemo(() => {
    return (checkins || []).map((checkin) => ({
      ...checkin,
      answeredAt: checkin.answeredAt || '',
      startDate: checkin.dueDate,
    }));
  }, [checkins]);

  const renderCheckins = useCallback(
    (row: CheckinSummary) => {
      const endDate = _get(row, 'dueDate');
      const submittedDate = _get(row, 'answeredAt');

      const endDateFormatted = endDate
        ? format(new Date(endDate), defaultCheckinDateFormat)
        : 'xxx';
      const submittedDateFormatted = submittedDate
        ? format(new Date(submittedDate), defaultCheckinDateFormat)
        : 'Pending';

      const urlToGo = isLoggedUserCheckinsPage
        ? getEndpointWithParams(Paths.MY_CHECKIN_DETAILS, { checkinId: row.id })
        : getEndpointWithParams(Paths.USER_CHECKIN_DETAILS, { userId, checkinId: row.id });

      const isReviewed = row.isReviewed
        ? CheckinButtonLabel.REVIEWED
        : CheckinButtonLabel.NOT_REVIEWED;

      const isPendingForAdmin = row.answeredAt ? isReviewed : CheckinButtonLabel.PENDING_FOR_ADMIN;
      const isPendingForUser = row.answeredAt
        ? CheckinButtonLabel.SUBMITED
        : CheckinButtonLabel.PENDING_FOR_USER;

      const buttonLabel = !isLoggedUserCheckinsPage ? isPendingForAdmin : isPendingForUser;

      return (
        <CustomTableRow key={row.id}>
          <CustomTableCell data-testid="checkin-period">{endDateFormatted}</CustomTableCell>
          <CustomTableCell data-testid="checkin-submitted-date">
            {submittedDateFormatted}
          </CustomTableCell>
          <CustomTableCell align="right">
            <Button
              variant="outlined"
              component={RouterLink}
              to={urlToGo}
              disabled={!row.answeredAt && !isLoggedUserCheckinsPage}
              data-testid="user-checkins-action-button"
            >
              {buttonLabel}
            </Button>
          </CustomTableCell>
        </CustomTableRow>
      );
    },
    [isLoggedUserCheckinsPage, userId]
  );

  const title = useMemo(() => {
    if (isLoggedUserCheckinsPage) return 'My Check-ins';

    return `${username} Check-ins`;
  }, [isLoggedUserCheckinsPage, username]);

  return (
    <Box data-testid="user-checkins-list-page">
      {!isLoggedUserCheckinsPage && (
        <CustomLink to={Paths.USERS} variant="subtitle1">
          go back
        </CustomLink>
      )}

      <Box sx={styles.header}>
        <PageTitle title={title} data-testid="user-checkins-title" sx={styles.title} />
      </Box>

      <CustomTable<CheckinSummary>
        data={checkinsFormatted}
        rowRender={renderCheckins}
        initialOrderBy="dueDate"
        headersArray={arrayHeaders}
        data-testid="user-checkins-list-table"
      />
    </Box>
  );
};

export default UserCheckinsPage;
